import React, { useState, useEffect } from 'react';
import { calculateRomanianDeadline, DEADLINE_PRESETS, CalculationType } from '../services/deadlineService';
import { X, Calendar as CalendarIcon, Calculator, ArrowRight, Save, Clock } from 'lucide-react';
import { CalendarEvent, CaseFile } from '../types';

interface DeadlineCalculatorModalProps {
  onClose: () => void;
  onSaveToCalendar?: (event: CalendarEvent) => void;
  cases?: CaseFile[];
}

const DeadlineCalculatorModal: React.FC<DeadlineCalculatorModalProps> = ({ onClose, onSaveToCalendar, cases }) => {
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [duration, setDuration] = useState<number>(15);
  const [calcType, setCalcType] = useState<CalculationType>('zile_libere');
  const [resultDate, setResultDate] = useState<Date | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [title, setTitle] = useState('Termen procedural calculat');
  const [selectedCaseId, setSelectedCaseId] = useState<string>('');

  useEffect(() => {
    if (startDate && duration) {
      const calculated = calculateRomanianDeadline(startDate, duration, calcType);
      setResultDate(calculated);
    }
  }, [startDate, duration, calcType]);

  const handlePresetChange = (presetId: string) => {
    setSelectedPreset(presetId);
    const preset = DEADLINE_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setDuration(preset.duration);
      setCalcType(preset.type);
      setTitle(preset.label);
    }
  };

  const handleSave = () => {
    if (onSaveToCalendar && resultDate) {
      const event: CalendarEvent = {
        id: crypto.randomUUID(),
        title: title,
        date: resultDate.toISOString(),
        description: `Calculat automat de la data: ${startDate}, durata: ${duration} (${calcType.replace('_', ' ')})`,
        type: 'deadline',
        linkedCaseId: selectedCaseId || undefined,
        isAutoCalculated: true
      };
      onSaveToCalendar(event);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-start gap-8">
          
          {/* Partea stângă: Formular */}
          <div className="flex-1 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                  <Calculator className="w-6 h-6 text-indigo-600" />
                  Calculator Termene
                </h3>
                <p className="text-sm text-slate-500 font-medium mt-1">Conform Codurilor de Procedură (RO)</p>
              </div>
              <button onClick={onClose} title="Închide calculatorul" aria-label="Închide" className="md:hidden p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="preset-select" className="block text-xs font-bold text-slate-700 uppercase mb-2">Tip Termen (Șabloane)</label>
                <select 
                  id="preset-select"
                  title="Selectează șablonul de termen"
                  aria-label="Selectează șablonul de termen"
                  value={selectedPreset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                >
                  <option value="">-- Personalizat --</option>
                  {DEADLINE_PRESETS.map(preset => (
                    <option key={preset.id} value={preset.id}>{preset.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="start-date-input" className="block text-xs font-bold text-slate-700 uppercase mb-2">Data de Start</label>
                  <div className="relative">
                    <CalendarIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input 
                      id="start-date-input"
                      type="date"
                      title="Data de start"
                      aria-label="Data de start"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="duration-input" className="block text-xs font-bold text-slate-700 uppercase mb-2">Durată</label>
                  <div className="flex gap-2">
                    <input 
                      id="duration-input"
                      type="number"
                      min="1"
                      title="Durata"
                      aria-label="Durata"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                      className="w-20 bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none text-center"
                    />
                    <select 
                      title="Tipul de calcul"
                      aria-label="Tipul de calcul"
                      value={calcType}
                      onChange={(e) => setCalcType(e.target.value as CalculationType)}
                      className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      <option value="zile_libere">Zile Libere</option>
                      <option value="zile_calendaristice">Zile Calendaristice</option>
                      <option value="luni">Luni</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Partea dreaptă: Rezultat */}
          <div className="md:w-64 bg-indigo-50 rounded-2xl p-6 flex flex-col justify-center border border-indigo-100/50 shrink-0 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
            
            <p className="text-xs font-bold text-indigo-600/80 uppercase tracking-wider mb-2 relative z-10 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Data Împlinirii
            </p>
            
            <div className="relative z-10">
              {resultDate ? (
                <>
                  <div className="text-4xl font-black text-indigo-900 tracking-tight leading-none mb-1">
                    {resultDate.getDate().toString().padStart(2, '0')}
                  </div>
                  <div className="text-lg font-bold text-indigo-700/90 capitalize mb-4">
                    {resultDate.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' })}
                  </div>
                  
                  <div className="text-sm font-medium text-indigo-800/80 bg-indigo-100/50 py-2 px-3 rounded-lg border border-indigo-200/50 inline-block">
                    Pică într-o {resultDate.toLocaleDateString('ro-RO', { weekday: 'long' })}
                  </div>
                </>
              ) : (
                <div className="text-slate-400 italic text-sm">Calcul în curs...</div>
              )}
            </div>

            <button onClick={onClose} title="Închide calculatorul" aria-label="Închide" className="hidden md:flex absolute top-4 right-4 p-1.5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors z-20">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="w-full sm:w-auto flex-1 max-w-xs">
            {cases && cases.length > 0 && (
              <select 
                title="Selectează dosarul asociat"
                aria-label="Selectează dosarul asociat"
                value={selectedCaseId}
                onChange={(e) => setSelectedCaseId(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="">-- Fără dosar asociat --</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>{c.caseNumber} - {c.clientName}</option>
                ))}
              </select>
            )}
          </div>
          
          <button 
            onClick={handleSave}
            disabled={!resultDate}
            className="w-full sm:w-auto px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Save className="w-4 h-4" />
            Adaugă în Calendar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeadlineCalculatorModal;